import {
  LABEL_STYLES,
  NAME_MODES,
  UNIT_DISPLAY_RANGES,
  type RangeKey,
  type UnitDisplaySettings,
} from "./types";

export const DEFAULT_UNIT_DISPLAY: UnitDisplaySettings = {
  label_px: 12,
  speed_px: 12,
  show_speed: true,
  name_mode: "full",
  label_style: "card",
  card_opacity: 0.95,
  status_dot: false,
  icon_scale: 1,
};

const isKeyOf = <T extends object>(obj: T, value: unknown): value is keyof T =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(obj, value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const clampTo = (key: RangeKey, value: number): number => {
  const { min, max, step } = UNIT_DISPLAY_RANGES[key];
  const snapped = Math.round(value / step) * step;
  return Number.isFinite(value) ? Math.min(max, Math.max(min, Math.round(snapped * 100) / 100)) : DEFAULT_UNIT_DISPLAY[key];
};

const parseRange = (key: RangeKey, value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? clampTo(key, value) : DEFAULT_UNIT_DISPLAY[key];

export const sanitizeUnitDisplay = (raw: unknown): UnitDisplaySettings => {
  const source = isRecord(raw) ? raw : {};
  return {
    label_px: parseRange("label_px", source.label_px),
    speed_px: parseRange("speed_px", source.speed_px),
    icon_scale: parseRange("icon_scale", source.icon_scale),
    card_opacity: parseRange("card_opacity", source.card_opacity),
    show_speed: typeof source.show_speed === "boolean" ? source.show_speed : DEFAULT_UNIT_DISPLAY.show_speed,
    status_dot: typeof source.status_dot === "boolean" ? source.status_dot : DEFAULT_UNIT_DISPLAY.status_dot,
    name_mode: isKeyOf(NAME_MODES, source.name_mode) ? source.name_mode : DEFAULT_UNIT_DISPLAY.name_mode,
    label_style: isKeyOf(LABEL_STYLES, source.label_style) ? source.label_style : DEFAULT_UNIT_DISPLAY.label_style,
  };
};

export const isDefaultUnitDisplay = (settings: UnitDisplaySettings): boolean =>
  (Object.keys(DEFAULT_UNIT_DISPLAY) as (keyof UnitDisplaySettings)[]).every(
    (key) => settings[key] === DEFAULT_UNIT_DISPLAY[key],
  );
